import { makeStyles } from "@material-ui/core";
import React, { useContext, useMemo } from "react";

import { FullSite } from "../../../../dtos/site";
import { useForceRerender } from "../../../../hooks/useForceRerender";
import SignalRContext from "../../../../signalr/context";
import { StatsSummary } from "../StatsSummary";

import { INIT_SITE_STATS_RESULT } from "./consts";
import { StatsKey } from "./types";
import { StatsTable } from "./StatsTable";
import { groupSitesForStats } from "./groupSitesForStats";

const useStyles = makeStyles(() => ({
    container: {
        display: "flex",
        gap: "20px",
        flexWrap: "wrap",
    },
    typeContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        flex: "1 1 300px",
        minWidth: 0,
    },
}));

interface Props {
    sites: FullSite[];
    dealerId: number | null;
}

export function SitesStats({ sites, dealerId }: Props) {
    const classes = useStyles();
    const { alarmStatus, serviceStatus, roomState, uiConnected } = useContext(SignalRContext);

    // The `serviceStatus`, `alarmStatus` and `roomState` map references do not change when new data is received,
    // so this component will not re-render automatically.
    const tick = useForceRerender(2000);

    const {
        sitesOffline,
        sitesOnline,
        sitesWithAlarm,
        sitesWithRoomBoxLeftOpen,
        sitesWithRoomEntryUnauthorized,
        sitesWithRoomIdle,
        sitesWithRoomInUse,
        issueCount,
    } = useMemo(
        () => uiConnected
            ? groupSitesForStats({ sites, alarmStatus, serviceStatus, roomState })
            : INIT_SITE_STATS_RESULT,
        [sites, alarmStatus, serviceStatus, roomState, tick, uiConnected]
    );

    return (
        <div className={classes.container}>
            <div className={classes.typeContainer}>
                <StatsSummary
                    label="Sites Total"
                    count={sites.length}
                />

                <StatsTable
                    sitesByStatsKey={{
                        [StatsKey.AllSites]: sites,
                        [StatsKey.OnlineSites]: sitesOnline,
                        [StatsKey.RoomInUse]: sitesWithRoomInUse,
                        [StatsKey.RoomIdle]: sitesWithRoomIdle,
                    }}
                    dealerId={dealerId}
                />
            </div>

            <div className={classes.typeContainer}>
                <StatsSummary
                    label="Issues Detected"
                    attentionToCount={true}
                    count={issueCount}
                />

                <StatsTable
                    itemLabel="issue"
                    attentionToCount={true}
                    sitesByStatsKey={{
                        [StatsKey.ActiveAlarm]: sitesWithAlarm,
                        [StatsKey.SiteOffline]: sitesOffline,
                        [StatsKey.RoomBoxLeftOpen]: sitesWithRoomBoxLeftOpen,
                        [StatsKey.RoomEntryUnauthorized]: sitesWithRoomEntryUnauthorized,
                    }}
                    dealerId={dealerId}
                />
            </div>
        </div>
    );
}
